import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as SurveyApi from '../../../api/PreformanceManagement/Survey';
import { SurveyResponseInput } from '../../../models/preformanceManagement';
import { BarChart3 } from 'lucide-react';
import AppIcon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { PageLayout } from '../../../components/ui/PageLayout';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import PrivacyIndicator from './components/PrivacyIndicator';
import RatingScale from './components/RatingScale';
import FeedbackTextArea from './components/FeedbackTextArea';
import ProgressIndicator from './components/ProgressIndicator';
import SubmissionConfirmation from './components/SubmissionConfirmation';
import WhatWeLearnedForEmployees from './components/WhatWeLearnedForEmployees';

type SurveyConfigType = {
  title: string;
  subtitle: string;
  month: string;
  question: string;
  feedbackPlaceholder: string;
};

const ENPSPulseSurvey: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [rating, setRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  const surveyConfig: SurveyConfigType = {
    title: t('performance.espn.pageTitle'),
    subtitle: t('performance.espn.pageSubtitle'),
    month: new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric' }).format(new Date()),
    question: t('performance.espn.ratingQuestion'),
    feedbackPlaceholder: t('performance.espn.feedbackQuestion'),
  };

  const totalSteps = 2;
  const steps = [t('performance.espn.ratingStepLabel', 'Rating'), t('performance.espn.feedbackStepLabel', 'Feedback')];

  const checkSubmission = async () => {
    const result = await SurveyApi.hasSubmittedSurvey();
    setAlreadySubmitted(result.payload?.hasSubmitted ?? false);
  };

  useEffect(() => {
    checkSubmission();
  }, []);

  const handleNext = () => {
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };
  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const canProceedToNext = () => (currentStep === 1 ? rating !== null : true);
  const canSubmit = () => rating !== null;

  const handleSubmit = async () => {
    if (!canSubmit()) return;

    setIsSubmitting(true);
    try {
      const payload: SurveyResponseInput = {
        rating: rating!,
        feedback: feedback.trim() || undefined,
        surveyMonth: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      };

      const result = await SurveyApi.submitSurveyResponse(payload);

      if (result.status === 200 && result.payload) {
        setShowConfirmation(true);
      } else {
        const errorMessage =
          result.errors?.map((e: { message: string }) => e.message).join(', ') ||
          t('performance.espn.submitError', 'Failed to submit survey');
        showToast(errorMessage, 'error');
      }
    } catch {
      showToast(t('performance.espn.submitErrorGeneric', 'Something went wrong. Please try again.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageLayout
        title={surveyConfig.title}
        description={`${surveyConfig.subtitle} \u2022 ${surveyConfig.month}`}
        icon={BarChart3}
        accentColor="rose"
        maxWidth="1600px"
      >
        <ToastRenderer toast={toast} onDismiss={dismissToast} />

        {alreadySubmitted ? (
          <div className="mt-12 max-w-[720px] mx-auto">
            <div className="text-center">
              <AppIcon name="CheckCircle" size={48} className="text-emerald-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground">
                {t('performance.espn.alreadySubmittedTitle')}
              </h2>
              <p className="text-sm text-muted-foreground mt-4 mb-8">
                {t('performance.espn.alreadySubmittedDesc')}
              </p>
            </div>
            <WhatWeLearnedForEmployees />
          </div>
        ) : (
          <div className="w-full">
            <PrivacyIndicator />
              <ProgressIndicator currentStep={currentStep} steps={steps} />

              {currentStep === 1 && (
                <div className="bg-card border border-border rounded-xl p-6 mb-6">
                  <RatingScale value={rating} onChange={setRating} question={surveyConfig.question} />
                  <div className="flex justify-between items-center mt-4">
                    <span className="text-sm text-muted-foreground">
                      {t('performance.espn.stepOf', { current: 1, total: totalSteps })}
                    </span>
                    <Button
                      onClick={handleNext}
                      disabled={!canProceedToNext()}
                      iconName="ArrowRight"
                      iconPosition="right"
                    >
                      {t('performance.espn.next')}
                    </Button>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="bg-card border border-border rounded-xl p-6 mb-6">
                  <FeedbackTextArea
                    value={feedback}
                    onChange={setFeedback}
                    placeholder={surveyConfig.feedbackPlaceholder}
                  />
                  <div className="flex justify-between mt-4">
                    <Button variant="outline" onClick={handlePrevious} iconName="ArrowLeft">
                      {t('performance.espn.back')}
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={!canSubmit() || isSubmitting}
                      loading={isSubmitting}
                      iconName="Send"
                      iconPosition="right"
                    >
                      {t('performance.espn.submit')}
                    </Button>
                  </div>
                </div>
              )}

              <div className="border border-border rounded-lg p-4 mt-6">
                <div className="flex items-start">
                  <AppIcon name="Lightbulb" size={20} className="text-emerald-500 me-3 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-2">
                      {t('performance.espn.feedbackImpactTitle')}
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {t('performance.espn.feedbackImpactDesc')}
                    </p>
                  </div>
                </div>
              </div>

              <WhatWeLearnedForEmployees />
            </div>
        )}
      </PageLayout>

      <SubmissionConfirmation
        open={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
          checkSubmission();
        }}
      />
    </>
  );
};

export default ENPSPulseSurvey;
